type RazorpayHandlerResponse = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

type RazorpayFailureResponse = {
  error?: {
    code?: string;
    description?: string;
    reason?: string;
  };
};

type RazorpayOptions = {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name?: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  handler: (response: RazorpayHandlerResponse) => void;
  modal?: { ondismiss?: () => void };
};

type RazorpayInstance = {
  open: () => void;
  on: (event: 'payment.failed', handler: (response: RazorpayFailureResponse) => void) => void;
};

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

export type { RazorpayHandlerResponse };

export async function loadRazorpay(): Promise<boolean> {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Opens Razorpay checkout and resolves on success.
 * Rejects when the user closes the modal or payment fails.
 */
export async function openRazorpayCheckout(
  options: Omit<RazorpayOptions, 'handler' | 'modal'> & {
    handler?: (response: RazorpayHandlerResponse) => void;
  },
): Promise<RazorpayHandlerResponse> {
  const ready = await loadRazorpay();
  const RazorpayCtor = window.Razorpay;
  if (!ready || !RazorpayCtor) {
    throw new Error('Razorpay SDK failed to load');
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    const rzp = new RazorpayCtor({
      ...options,
      handler: (response) => {
        options.handler?.(response);
        finish(() => resolve(response));
      },
      modal: {
        ondismiss: () => {
          finish(() => reject(new Error('Payment cancelled')));
        },
      },
    });

    rzp.on('payment.failed', (response) => {
      const message =
        response?.error?.description ||
        response?.error?.reason ||
        'Payment failed';
      finish(() => reject(new Error(message)));
    });

    rzp.open();
  });
}
